/**
 * Version Tags Cell Component
 *
 * Renders the tags column for version tables.
 * Displays both system tags (current/previous) and custom user tags with colors.
 */

import React from 'react';
import { Tag } from 'lucide-react';
import { TagBadge, LabelBadge } from '@loader/ui';
import type { SystemTag } from '@loader/shared-types';

interface VersionTagsCellProps {
  /** System-assigned version lifecycle status */
  system_tag: 'current' | 'previous' | null;
  /** User-assigned custom tags */
  tags: Array<{
    id: string;
    name: string;
    color: string | null;
    description?: string | null;
  }>;
}

/**
 * Version Tags Cell Component
 *
 * Displays system tag (current/previous) and custom tags for a version.
 * System tags are shown as LabelBadge with GitBranch icon.
 * Custom tags are shown as TagBadge with Tag icon and color support.
 */
export const VersionTagsCell: React.FC<VersionTagsCellProps> = ({ system_tag, tags }) => {
  const hasSystemTag = system_tag !== null;
  const hasCustomTags = tags && tags.length > 0;

  // No tags at all
  if (!hasSystemTag && !hasCustomTags) {
    return (
      <div className="text-muted-foreground flex items-center gap-1">
        <Tag className="h-3 w-3" />
        <span className="text-xs">No tags</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {/* System tag (current or previous) */}
      {hasSystemTag && <LabelBadge label={system_tag as SystemTag} />}

      {/* Custom user tags */}
      {hasCustomTags &&
        tags.map((tag) => (
          <TagBadge key={tag.id} name={tag.name} color={tag.color} size="sm" />
        ))}
    </div>
  );
};
